import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Stack, Tab, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { filterStatType } from "../../../../../modules/stats/server/analytics-service";
import { calculatePercentage } from "../../utils";
import { AnalyticsSendsTableExpandedInteractionsByLink } from "./expanded-interactions-by-link";
import { AnalyticsSendsTableExpandedInteractionsByPerson } from "./expanded-interactions-by-person";

import type { GroupedMomentAnalyticsData } from "../../../../../modules/stats/server/analytics-service";
import type { FC, SyntheticEvent } from "react";

export const AnalyticsSendsTableExpandedPageInteractions: FC<{
  stats: GroupedMomentAnalyticsData["stats"];
}> = ({ stats }) => {
  const [tab, setTab] = useState<"person" | "link">("person");
  const handleChange = useCallback((_: SyntheticEvent, value: "person" | "link") => {
    setTab(value);
  }, []);

  const { interactions, interactingVisitors, totalVisitors } = useMemo(() => {
    const clicks = filterStatType("click", stats);
    const visitorIds = new Set(
      clicks.map(
        ({ data }) =>
          data.recipient_data?.person_id ??
          data.recipient_data?.destination ??
          data.recipient_data?.id ??
          "unknown",
      ),
    );

    return {
      interactions: clicks.reduce((total, { data }) => total + data.count, 0),
      interactingVisitors: visitorIds.size,
      totalVisitors: filterStatType("delivery", stats).length,
    };
  }, [stats]);

  const interactionPercentage = calculatePercentage(interactingVisitors, totalVisitors);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="baseline" spacing={2}>
        <Typography variant="h4">{`${interactionPercentage}%`}</Typography>
        <Typography variant="body2" color="text.secondary">
          of visitors ({interactingVisitors}/{totalVisitors}) clicked on any link on the page (
          {interactions} interactions in total).
        </Typography>
      </Stack>
      <TabContext value={tab}>
        <TabList onChange={handleChange}>
          <Tab label="By person" value="person" />
          <Tab label="By link" value="link" />
        </TabList>
        <TabPanel value="person">
          <AnalyticsSendsTableExpandedInteractionsByPerson stats={stats} isLinkedPage />
        </TabPanel>
        <TabPanel value="link">
          <AnalyticsSendsTableExpandedInteractionsByLink stats={stats} isLinkedPage />
        </TabPanel>
      </TabContext>
    </Stack>
  );
};
